// src/components/congressional/TradeFeedTab.jsx
// Congressional Trade Tracker — Tab 1 (Trade Feed).
// Extracted from CongressPage.jsx (redesign design doc Section on file
// organization) — same reasoning as the backend split: this page was about
// to grow a 4th tab plus real feature work on the existing three, and one
// growing file becomes the same "too big to safely edit" problem
// congress.js just hit. Matches this codebase's own existing precedent for
// splitting a multi-tab page (ReviewConsoleTab.jsx/TradeBrowserTab.jsx, Ops
// repo) rather than inventing a new pattern.
//
// THIS PASS (owner request — standard filter/pagination UX across every tab):
// filter row replaced with the shared ConFilterBar, prev/next replaced with
// the shared ConPaginationFooter, and page size is now user-selectable
// (10/25/50/100, default 10) instead of a fixed 25. Same components now
// power Member Profiles too — new tabs get this for free by importing them.
//
// EARLIER PASS ADDS (CONGRESSIONAL-PAGE-REDESIGN-DESIGN.md item 7), unchanged:
//   - Member name is now a link to the Member Detail Subpage (via onNavigate),
//     when BIOGUIDEID is present. Candidate-filer rows with no resolved
//     member render as plain text — never a link to nowhere.
//   - Ownership-type tooltip: OWNER already displayed, now with the real
//     conviction-multiplier explanation on hover (design doc's "show your
//     work" transparency feature).
//   - Bipartisan Convergence badge: a lightweight approximation, not a
//     per-row backend flag — fetches /insights/convergence once for the
//     current default window (90d) and badges any row whose TICKER appears
//     in that result set. Labeled "Cluster: cross-party activity," explicitly
//     NOT styled or worded like a D3 signal — see design doc Section 8's
//     boundary on this.
//   - Current-party delta: shown only when a trade's historical party
//     differs from the member's current party (e.g. a mid-term switch).

import { useState, useEffect, useCallback } from 'react';
import { Landmark, ExternalLink } from 'lucide-react';
import { API_URL } from '../../styles/shared';
import TickerStatusBadge from './TickerStatusBadge';
import ConFilterBar, { ConPaginationFooter } from './ConFilterBar';
import { DEFAULT_PAGE_SIZE, PARTY_COLORS, TYPE_COLORS, OWNER_LABELS, fmtDate, Card, authHeaders } from './congressShared';

export default function TradeFeedTab({ token, onRequestInvite, onNavigate }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isGuest, setIsGuest] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({ chamber: '', party: '', ticker: '', member: '', transactionType: '', window: 'ytd', status: 'active', category: '' });
  const [pendingFilters, setPendingFilters] = useState(filters);

  // Cluster-context ticker set for the Bipartisan Convergence badge — see
  // file header for why this is a lightweight approximation, not a per-row
  // backend flag.
  const [convergenceTickers, setConvergenceTickers] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });

    try {
      const r = await fetch(API_URL + '/api/congress/trades?' + qs.toString(), { headers: authHeaders(token) });
      const d = await r.json().catch(() => ({ success: false, message: 'Invalid response from server' }));
      if (!d.success) {
        setError(d.message || 'Could not load congressional trade data.');
      } else {
        setRows(d.data.rows || []);
        setTotal(d.data.total || 0);
        setIsGuest(!!d.data.isGuest);
      }
    } catch (e) {
      setError('Could not reach the server.');
    }
    setLoading(false);
  }, [page, pageSize, filters, token]);

  useEffect(() => { load(); }, [load]);

  // Fetch convergence tickers once (default 90d window, generous limit) —
  // not re-fetched per filter/page change, since this is a coarse
  // "cluster activity happened somewhere in this ticker recently" signal,
  // not a precise per-row match. Failure is silent (no badge shown) rather
  // than surfacing a second error banner for a non-critical enrichment.
  useEffect(() => {
    fetch(API_URL + '/api/congress/insights/convergence?limit=50', { headers: authHeaders(token) })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setConvergenceTickers(new Set(d.data.rows.map(r => r.TICKER)));
        }
      })
      .catch(() => { /* silent — badge just doesn't show */ });
  }, [token]);

  const applyFilters = () => { setPage(1); setFilters(pendingFilters); };
  const clearFilters = () => {
    const empty = { chamber: '', party: '', ticker: '', member: '', transactionType: '', window: 'ytd', status: 'active', category: '' };
    setPendingFilters(empty); setFilters(empty); setPage(1);
  };
  const changePageSize = (e) => { setPage(1); setPageSize(parseInt(e.target.value, 10)); };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // A guest is capped to GUEST_LIMIT rows on page 1 only (backend-enforced) —
  // pagination controls are meaningless for a guest, since there's never a
  // page 2 to go to. Matches ScreenerPage.jsx's own real pattern: no top
  // banner at all, real results shown, one honest locked notice at the end
  // stating exactly how many more exist.
  const hiddenCount = isGuest ? Math.max(0, total - rows.length) : 0;

  return (
    <div>
      <ConFilterBar
        showChamber chamber={pendingFilters.chamber} onChamberChange={e => setPendingFilters(p => ({ ...p, chamber: e.target.value }))}
        showParty party={pendingFilters.party} onPartyChange={e => setPendingFilters(p => ({ ...p, party: e.target.value }))}
        showTicker ticker={pendingFilters.ticker} onTickerChange={v => setPendingFilters(p => ({ ...p, ticker: v }))}
        showMemberName memberName={pendingFilters.member} onMemberNameChange={v => setPendingFilters(p => ({ ...p, member: v }))}
        showTransactionType transactionType={pendingFilters.transactionType} onTransactionTypeChange={e => setPendingFilters(p => ({ ...p, transactionType: e.target.value }))}
        showCategory category={pendingFilters.category} onCategoryChange={e => setPendingFilters(p => ({ ...p, category: e.target.value }))}
        showStatus status={pendingFilters.status} onStatusChange={e => setPendingFilters(p => ({ ...p, status: e.target.value }))}
        showWindow dateWindow={pendingFilters.window} onWindowChange={e => setPendingFilters(p => ({ ...p, window: e.target.value }))}
        showPageSize pageSize={pageSize} onPageSizeChange={changePageSize}
        onApply={applyFilters} onClear={clearFilters}
      />

      {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: '8px', padding: '0.75rem 1rem', color: '#ef4444', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>No trades match these filters.</div>
      ) : (
        <>
          <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            {isGuest
              ? `Showing ${rows.length} of ${total.toLocaleString()} total trades`
              : `${total.toLocaleString()} trade${total !== 1 ? 's' : ''} · page ${page} of ${totalPages}`}
          </p>
          {rows.map(row => {
            const isRotation = row.TICKER === '--';
            const isConvergence = !isRotation && convergenceTickers.has(row.TICKER);
            return (
              <Card key={row.ID}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                  <span style={{ color: '#f1f5f9', fontWeight: 700, fontFamily: 'monospace', fontSize: '0.95rem' }}>
                    {isRotation ? <Landmark size={14} style={{ verticalAlign: '-2px', marginRight: '0.3rem', color: '#00d4ff' }} /> : null}
                    {isRotation ? (row.ASSETCATEGORY || 'Rotation') : row.TICKER}
                  </span>
                  <TickerStatusBadge status={row.TICKERRESOLUTIONSTATUS} />
                  {isConvergence && (
                    <span title="Members from both parties traded this ticker within a similar window — cluster context, not a trading signal." style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, background: 'rgba(167,139,250,0.12)', color: '#a78bfa' }}>
                      Cluster: cross-party activity
                    </span>
                  )}
                  <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, background: (TYPE_COLORS[row.TRANSACTIONTYPE] || '#64748b') + '22', color: TYPE_COLORS[row.TRANSACTIONTYPE] || '#64748b' }}>
                    {row.TRANSACTIONTYPE || '—'}
                  </span>
                  {row.CONVICTIONWEIGHT ? (
                    <span title="Conviction weight — committee role + ownership + recency, not just raw amount" style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, background: 'rgba(0,255,136,0.12)', color: '#00ff88' }}>
                      Conviction {Number(row.CONVICTIONWEIGHT).toFixed(2)}
                    </span>
                  ) : null}
                </div>
                <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.4rem' }}>
                  {row.TICKERDESCRIPTION || ''}
                </div>
                <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.78rem', color: '#64748b' }}>
                  {row.BIOGUIDEID ? (
                    <button
                      onClick={() => onNavigate('congressMember', { bioguideId: row.BIOGUIDEID, chamber: row.CHAMBER })}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: PARTY_COLORS[row.PARTY] || '#94a3b8', fontWeight: 700, textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.2)' }}
                    >
                      {row.SENATOR}
                    </button>
                  ) : (
                    // No resolved member (candidate filer, etc.) — plain text,
                    // never a link to a page that would 404.
                    <strong style={{ color: PARTY_COLORS[row.PARTY] || '#94a3b8' }}>{row.SENATOR}</strong>
                  )}
                  <span>
                    {row.CHAMBER} {row.PARTY ? `(${row.PARTY})` : ''}
                    {/* Current-party delta — shown ONLY when it differs from
                        the trade's own historical party, e.g. a mid-term
                        party switch. Not a second badge on every row. */}
                    {row.CURRENTPARTY && row.PARTY && row.CURRENTPARTY !== row.PARTY && (
                      <span title="This member's party at the time of this filing differs from their current party" style={{ color: '#f59e0b' }}>
                        {' '}→ now {row.CURRENTPARTY}
                      </span>
                    )}
                  </span>
                  <span>{fmtDate(row.TRANSACTIONDATE)}</span>
                  <span>{row.AMOUNTRANGE || '—'}</span>
                  <span title={OWNER_LABELS[row.OWNER] || row.OWNER || 'SELF'}>{row.OWNER || 'SELF'}</span>
                  {row.FILINGLAGDAYS != null && <span>Filed {row.FILINGLAGDAYS}d late</span>}
                  {row.SOURCEURL && (
                    <a href={row.SOURCEURL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#00d4ff', textDecoration: 'none' }}>
                      Source filing <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              </Card>
            );
          })}

          {isGuest ? (
            hiddenCount > 0 && (
              <div style={{ padding: '1.75rem 1rem', textAlign: 'center', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', marginTop: '0.5rem' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔒</div>
                <div style={{ fontSize: '0.95rem', color: '#f1f5f9', fontWeight: 700, marginBottom: '0.4rem' }}>
                  {hiddenCount.toLocaleString()} more trade{hiddenCount !== 1 ? 's' : ''} for members
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', maxWidth: '360px', margin: '0 auto 1rem' }}>
                  Members see full trade history back to 2020, every filter, and every chamber — not just this page.
                </div>
                <button onClick={onRequestInvite} style={{ padding: '0.6rem 1.6rem', borderRadius: '8px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 700, fontSize: '0.85rem', fontFamily: 'inherit' }}>
                  Request Invite → See All Trades
                </button>
              </div>
            )
          ) : (
            <ConPaginationFooter
              page={page} totalPages={totalPages}
              onPrev={() => setPage(p => Math.max(1, p - 1))}
              onNext={() => setPage(p => Math.min(totalPages, p + 1))}
              showPageSize pageSize={pageSize} onPageSizeChange={changePageSize}
            />
          )}
        </>
      )}
    </div>
  );
}
